<template>
    <tr class="combinations-table-row">
        <td style="width: 40%">
            <SelectFromDataSourceInput
                ref="shiftSelect"
                v-model="shiftId"
                :dataSource="resourceDataSource"
                @update:model-value="onUpdateShift"
                :formatter="
                    (e) => {
                        return e.name;
                    }
                "
                :keyProp="'id'"
                :lazyInit="false"
                :showAllText="i18n('elements.select.pleaseSelect')"
            />
        </td>
        <td>
            <TimePicker
                ref="standardServiceDurationPicker"
                :options="{ maxHours: 24, showSeconds: false }"
                :model-value="overwrite.standardServiceDuration"
                @update:model-value="onUpdatePreferredMinOccupancy"
            />
        </td>
        <td>
            <Button
                aria-label="Cancel"
                class="btn-danger"
                icon="la la-trash"
                style="background-color: #fd397a; border-color: #fd397a"
                @click="removeOverwrite(overwrite)"
            />
        </td>
    </tr>
</template>

<script lang="ts" setup>
    import { Factory } from "@/data/dao/Factory";
    import type { IDataSource } from "@/data/dao/service/BaseServiceDao";
    import { CatalogDao } from "@/data/dao/service/CatalogDao";
    import type { IStandardServiceDurationOverwriteCreateOrUpdate } from "@/data/model/ITableCombination";
    import type { IScheduleShift } from "@/data/model/OfferSchedule";
    import { i18n } from "@/portal/Trans";
    import { logError } from "@/shared/logging/errors";
    import { ref } from "vue";

    import SelectFromDataSourceInput from "@/portal/components/elements/SelectFromDataSourceInput.vue";
    import TimePicker from "@/portal/components/elements/TimePicker.vue";
    import Button from "primevue/button";

    const props = defineProps<{
        overwrite: IStandardServiceDurationOverwriteCreateOrUpdate;
    }>();

    const shiftId = ref<string | undefined>(props.overwrite.shiftId);
    const shiftSelect = ref<InstanceType<typeof SelectFromDataSourceInput> | null>(null);
    const standardServiceDurationPicker = ref<InstanceType<typeof TimePicker> | null>(null);

    const emit = defineEmits(["onRemoveOverwrite", "onUpdateShift"]);

    const catalogDao = Factory.getInstance<CatalogDao>(CatalogDao);
    const resourceDataSource: IDataSource<IScheduleShift> = {
        getData: async () => {
            try {
                const resources = await catalogDao.getScheduleShifts({});
                return {
                    limit: 0,
                    offset: 0,
                    hasNext: false,
                    hasPrev: false,
                    items: resources,
                };
            } catch (ex) {
                logError(ex);
                throw ex;
            }
        },
    };

    function removeOverwrite(combinationToRemove: IStandardServiceDurationOverwriteCreateOrUpdate) {
        emit("onRemoveOverwrite", combinationToRemove);
    }

    function onUpdateShift(shiftId: string | undefined) {
        emit("onUpdateShift", shiftId);
    }

    function onUpdatePreferredMinOccupancy(value: string | undefined) {
        props.overwrite.standardServiceDuration = value;
    }

    function validate(): boolean {
        let hasErrors = false;

        shiftSelect.value!.setError(false);
        standardServiceDurationPicker.value!.setError(false);

        if (props.overwrite.shiftId == null || props.overwrite.shiftId.length === 0) {
            shiftSelect.value!.setError(true);
            hasErrors = true;
        }

        if (props.overwrite.standardServiceDuration == null || props.overwrite.standardServiceDuration.length === 0) {
            standardServiceDurationPicker.value!.setError(true);
            hasErrors = true;
        }

        return !hasErrors;
    }

    defineExpose({
        validate
    });
</script>

<style lang="scss" scoped>
    .combinations-table-row {
        td {
            padding: 7.5px;
        }
    }
</style>
